import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { INote, NoteContextType } from '@dataTypes/note.type'
import {
  NoteDraft,
  NoteGridRow,
  NoteUpdateItem,
  NoteUser,
} from '../NoteTextEditor.types'

type UseNoteEditorStateArgs = {
  patientId: string
  contextId?: string
  noteData?: {
    title?: string
    content?: string
    tag?: string
    date?: Date
  }
  notes?: NoteGridRow[]
  viewMode: boolean
  onUpdate?: (data: NoteUpdateItem[]) => void
}

type UseNoteEditorStateResult = {
  data: NoteDraft
  notesData: NoteUpdateItem[]
  /** Fingerprint of rows shown in Saved Notes; use as React `key` on the grid so Syncfusion refreshes reliably. */
  savedNotesGridKey: string
  defaultTag: string
  editingNoteIndex: number | null
  viewOnly: boolean
  setData: Dispatch<SetStateAction<NoteDraft>>
  setViewOnly: Dispatch<SetStateAction<boolean>>
  setNotesData: Dispatch<SetStateAction<NoteUpdateItem[]>>
  handleSaveNote: () => void
  handleAddNote: () => void
  handleEditNote: (props: NoteGridRow) => void
  handleViewNote: (props: NoteGridRow) => void
  handleDeleteNote: (props: NoteGridRow) => void
}

/** Stable string when list identity / content changes; used for parent sync and grid remount key. */
export const getNotesListSyncFingerprint = (
  list: NoteGridRow[] | undefined,
): string => {
  const arr = list ?? []
  return JSON.stringify(
    arr.map((n) => {
      const body =
        typeof n.content === 'string'
          ? n.content
          : typeof n.body === 'string'
            ? n.body
            : ''
      const head = body.slice(0, 48)
      const tail = body.length > 48 ? body.slice(-48) : ''
      return {
        i: n.note_id ?? null,
        g: n.note_group_id ?? null,
        u: n.date_time_updated_at ?? null,
        t: n.title ?? null,
        v: n.version ?? null,
        b: body.length,
        h: head,
        l: tail,
      }
    }),
  )
}

const getInitialData = (
  noteData?: UseNoteEditorStateArgs['noteData'],
): NoteDraft => ({
  title: noteData?.title ?? 'Untitled',
  content: noteData?.content ?? '',
  tag: noteData?.tag ?? '',
  date: noteData?.date ?? new Date(),
})

const resolveNoteTag = (props: NoteGridRow): string =>
  (Array.isArray(props.tags) && props.tags[0] != null
    ? String(props.tags[0])
    : (props.context_type ?? props.tag ?? '')) ?? ''

const resolveNoteDate = (props: NoteGridRow): Date => {
  if (props.date instanceof Date) return props.date
  if (props.date_time_created_at instanceof Date)
    return props.date_time_created_at
  if (props.date_time_updated_at instanceof Date)
    return props.date_time_updated_at

  const value =
    props.date ?? props.date_time_created_at ?? props.date_time_updated_at
  const parsedDate = new Date(value ?? new Date())
  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate
}

const getNoteUpdatedAtMs = (n: NoteUpdateItem): number => {
  const row = n as NoteGridRow
  const tryParse = (v: Date | string | undefined): number | null => {
    if (v == null) return null
    const t = v instanceof Date ? v.getTime() : new Date(v).getTime()
    return Number.isNaN(t) ? null : t
  }
  return (
    tryParse(row.date_time_updated_at) ??
    tryParse(row.date) ??
    tryParse(row.date_time_created_at) ??
    0
  )
}

/** Newest `date_time_updated_at` first; ties broken by created / local `date`. Re-assigns `index` for grid row actions. */
const sortNotesByDateUpdatedDesc = (
  list: NoteUpdateItem[],
): NoteUpdateItem[] => {
  const sorted = [...list].sort(
    (a, b) => getNoteUpdatedAtMs(b) - getNoteUpdatedAtMs(a),
  )
  return sorted.map((item, index) => {
    const base = item as NoteGridRow
    return { ...base, index } as NoteUpdateItem
  })
}

const rowHasMeaningfulHtml = (html: string): boolean => {
  const stripped = html
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  return stripped.length > 0 || html.includes('<img')
}

/**
 * Restore inline rich-text draft from parent `notes` on remount (e.g. wizard step away/back).
 * Persisted rows (`note_id`) are listed in the grid; unsaved drafts only exist in `notes` and
 * must hydrate `data` or the Syncfusion editor shows empty while form state still has HTML.
 */
const getInitialEditorDataFromUnsavedNotes = (
  noteData: UseNoteEditorStateArgs['noteData'],
  notes: NoteGridRow[] | undefined,
): NoteDraft => {
  const base = getInitialData(noteData)
  const list = notes ?? []
  const unsaved = list.filter(
    (n) => !(typeof n.note_id === 'string' && n.note_id.trim() !== ''),
  ) as NoteUpdateItem[]
  if (unsaved.length === 0) {
    return base
  }
  const sorted = sortNotesByDateUpdatedDesc(unsaved)
  const row = sorted[0] as NoteGridRow
  const content = row.content ?? row.body ?? ''
  if (!rowHasMeaningfulHtml(content)) {
    return base
  }
  const tagFromRow = resolveNoteTag(row)
  return {
    title: row.title ?? base.title,
    content,
    tag: tagFromRow || base.tag,
    date: resolveNoteDate(row),
  }
}

export const useNoteEditorState = ({
  patientId,
  contextId,
  noteData,
  notes,
  viewMode,
  onUpdate,
}: UseNoteEditorStateArgs): UseNoteEditorStateResult => {
  const [data, setData] = useState<NoteDraft>(() =>
    getInitialEditorDataFromUnsavedNotes(noteData, notes),
  )
  const [notesData, setNotesData] = useState<NoteUpdateItem[]>(() =>
    sortNotesByDateUpdatedDesc(notes ?? []),
  )
  const [defaultTag] = useState<string>(noteData?.tag ?? '')
  const [editingNoteIndex, setEditingNoteIndex] = useState<number | null>(null)
  const [viewOnly, setViewOnly] = useState<boolean>(viewMode)

  const notesFromParentSyncKey = useMemo(
    (): string => getNotesListSyncFingerprint(notes),
    [notes],
  )

  const savedNotesGridKey = useMemo(
    (): string => getNotesListSyncFingerprint(notesData as NoteGridRow[]),
    [notesData],
  )

  const latestNotesFromParentRef = useRef(notes)
  latestNotesFromParentRef.current = notes

  useLayoutEffect((): void => {
    setNotesData(
      sortNotesByDateUpdatedDesc(latestNotesFromParentRef.current ?? []),
    )
    setEditingNoteIndex(null)
  }, [notesFromParentSyncKey])

  const user = useMemo<NoteUser>(
    () => JSON.parse(localStorage.getItem('user') || '{}') as NoteUser,
    [],
  )

  const resetData = useCallback((): void => {
    setData({
      title: 'Untitled',
      content: '',
      tag: defaultTag,
      date: new Date(),
    })
    setEditingNoteIndex(null)
  }, [defaultTag])

  const handleSaveNote = useCallback((): void => {
    if (editingNoteIndex !== null) {
      const updatedNotes = [...notesData]
      const existing = (updatedNotes[editingNoteIndex] ?? {}) as NoteUpdateItem
      updatedNotes[editingNoteIndex] = {
        ...existing,
        title: data.title,
        content: data.content,
        date: data.date,
      }
      const reordered = sortNotesByDateUpdatedDesc(updatedNotes)
      setNotesData(reordered)
      onUpdate?.(reordered)
    } else {
      const isClaims = defaultTag === NoteContextType.CLAIMS
      const resolvedContextId =
        isClaims && contextId?.trim()
          ? contextId.trim()
          : patientId && patientId.trim()
            ? patientId
            : null
      const newNote: Partial<INote> = {
        context_type: defaultTag as NoteContextType,
        title: data.title,
        body: data.content,
        context_id: resolvedContextId,
        created_by: user.user_id,
        updated_by: user.user_id,
        patient_id: patientId,
      }
      const nextNotes = sortNotesByDateUpdatedDesc([
        ...(notesData ?? []),
        newNote,
      ])
      setNotesData(nextNotes)
      onUpdate?.(nextNotes)
    }
    resetData()
  }, [
    contextId,
    data,
    defaultTag,
    editingNoteIndex,
    notesData,
    onUpdate,
    patientId,
    resetData,
    user.user_id,
  ])

  const handleAddNote = useCallback((): void => {
    resetData()
    setViewOnly(false)
  }, [resetData])

  const handleEditNote = useCallback((props: NoteGridRow): void => {
    setEditingNoteIndex(props.index ?? null)
    setData({
      title: props.title ?? 'Untitled',
      content: props.content ?? props.body ?? '',
      tag: resolveNoteTag(props),
      date: resolveNoteDate(props),
    })
    setViewOnly(false)
  }, [])

  const handleViewNote = useCallback((props: NoteGridRow): void => {
    setViewOnly(true)
    setData({
      title: props.title ?? 'Untitled',
      content: props.content ?? props.body ?? '',
      tag: resolveNoteTag(props),
      date: resolveNoteDate(props),
    })
  }, [])

  const handleDeleteNote = useCallback(
    (props: NoteGridRow): void => {
      const rowIndex = props.index
      if (typeof rowIndex !== 'number') return
      const updatedNotes = [...notesData]
      updatedNotes.splice(rowIndex, 1)
      const reordered = sortNotesByDateUpdatedDesc(updatedNotes)
      setNotesData(reordered)
      onUpdate?.(reordered)
    },
    [notesData, onUpdate],
  )

  useEffect(() => {
    // When the grid already shows persisted notes (note_id), the parent is the source of truth
    // (e.g. overlay sync → form state). Calling onUpdate(notesData) on every parent `notes`
    // change caused an infinite loop: child pushes → parent updates → new `notes` ref → repeat,
    // which shows as a stuck loader and rows flickering.
    const hasInitialNotes =
      notes?.some(
        (n) => typeof n.note_id === 'string' && n.note_id.trim() !== '',
      ) ?? false
    if (hasInitialNotes) {
      return
    }

    // Draft / inline-only flows: editor Save is hidden; propagate rich-text draft via `data`.
    const html = data.content ?? ''
    const strippedText = html
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim()
    const hasImage = html.includes('<img')

    onUpdate?.(strippedText.length > 0 || hasImage ? [data] : [])
  }, [data, notes, onUpdate])

  useEffect(() => {
    if (viewMode) {
      setData((prev) => ({
        ...prev,
        title: noteData?.title ?? prev.title,
        content: noteData?.content ?? prev.content,
        tag: noteData?.tag ?? prev.tag,
        date: noteData?.date ?? prev.date,
      }))
    }
  }, [
    noteData?.content,
    noteData?.date,
    noteData?.tag,
    noteData?.title,
    viewMode,
  ])

  return {
    data,
    notesData,
    savedNotesGridKey,
    defaultTag,
    editingNoteIndex,
    viewOnly,
    setData,
    setViewOnly,
    setNotesData,
    handleSaveNote,
    handleAddNote,
    handleEditNote,
    handleViewNote,
    handleDeleteNote,
  }
}
